export function initHomeHeroCarousel() {
    const carousels = document.querySelectorAll<HTMLElement>("[data-home-hero-carousel]");
    const TransitionMs = 700;

    carousels.forEach(root => {
        if (root.dataset.homeHeroReady === "true") {
            return;
        }

        const viewport = root.querySelector<HTMLElement>("[data-home-hero-viewport]");
        const track = root.querySelector<HTMLElement>("[data-home-hero-track]");
        const slides = track
            ? Array.from(track.querySelectorAll<HTMLElement>("[data-home-hero-slide]"))
            : [];

        if (!viewport || !track || slides.length === 0) {
            return;
        }

        root.dataset.homeHeroReady = "true";

        const previousButton = root.querySelector<HTMLButtonElement>("[data-home-hero-prev]");
        const nextButton = root.querySelector<HTMLButtonElement>("[data-home-hero-next]");
        const indicators = Array.from(root.querySelectorAll<HTMLButtonElement>("[data-home-hero-to]"));
        const slideCount = slides.length;
        const hasLoopClones = slideCount > 1;
        const configuredCloneCount = Number(root.dataset.homeHeroCloneCount ?? "1");
        const cloneCount = hasLoopClones
            ? Math.min(slideCount, Math.max(1, Number.isFinite(configuredCloneCount) ? configuredCloneCount : 1))
            : 0;
        const intervalMs = Number(root.dataset.homeHeroInterval ?? "10000");
        const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

        let activeIndex = 0;
        let trackIndex = cloneCount;
        let timer: number | undefined;
        let resetTimer: number | undefined;
        let shouldNormalizeAfterTransition = false;
        let swipeStartX: number | undefined;
        let swipeStartY: number | undefined;
        let suppressClick = false;

        function getStep() {
            const children = Array.from(track.children) as HTMLElement[];

            if (children.length === 0) {
                return 0;
            }

            const firstWidth = children[0].getBoundingClientRect().width;
            const trackStyles = window.getComputedStyle(track);
            const gap = parseFloat(trackStyles.columnGap || trackStyles.gap || "0");

            return firstWidth + (Number.isFinite(gap) ? gap : 0);
        }

        function setTrackPosition(animate: boolean) {
            const children = Array.from(track.children) as HTMLElement[];
            const current = children[trackIndex];

            if (!current) {
                return;
            }

            children.forEach(child => child.classList.toggle("is-current", child === current));

            track.classList.toggle("is-jumping", !animate);

            const slideWidth = current.getBoundingClientRect().width;
            const offset = (viewport.clientWidth - slideWidth) / 2 - (trackIndex * getStep());
            track.style.transform = `translate3d(${offset}px, 0, 0)`;

            if (!animate) {
                void track.offsetWidth;
                requestAnimationFrame(() => track.classList.remove("is-jumping"));
            }
        }

        function normalizeTrackPosition() {
            if (!hasLoopClones) {
                return;
            }

            shouldNormalizeAfterTransition = false;

            if (trackIndex < cloneCount || trackIndex >= cloneCount + slideCount) {
                trackIndex = cloneCount + activeIndex;
                setTrackPosition(false);
            }
        }

        function scheduleNormalize(animate: boolean) {
            if (resetTimer !== undefined) {
                window.clearTimeout(resetTimer);
                resetTimer = undefined;
            }

            if (!animate) {
                normalizeTrackPosition();
                return;
            }

            shouldNormalizeAfterTransition = trackIndex < cloneCount || trackIndex >= cloneCount + slideCount;

            if (!shouldNormalizeAfterTransition) {
                return;
            }

            resetTimer = window.setTimeout(() => {
                normalizeTrackPosition();
                resetTimer = undefined;
            }, TransitionMs + 300);
        }

        function updateState() {
            slides.forEach(slide => {
                slide.classList.toggle("is-active", slide.dataset.homeHeroIndex === activeIndex.toString());
            });

            indicators.forEach(indicator => {
                const isActive = indicator.dataset.homeHeroTo === activeIndex.toString();
                indicator.setAttribute("aria-current", isActive ? "true" : "false");
            });
        }

        function moveTo(nextIndex: number, animate = true) {
            if (slideCount === 1) {
                activeIndex = 0;
                trackIndex = 0;
                updateState();
                setTrackPosition(animate);
                return;
            }

            if (nextIndex < 0) {
                activeIndex = slideCount - 1;
                trackIndex = cloneCount - 1;
            }
            else if (nextIndex >= slideCount) {
                activeIndex = 0;
                trackIndex = cloneCount + slideCount;
            }
            else {
                activeIndex = nextIndex;
                trackIndex = cloneCount + nextIndex;
            }

            updateState();
            setTrackPosition(animate);
            scheduleNormalize(animate);
        }

        function restartTimer() {
            if (timer !== undefined) {
                window.clearInterval(timer);
                timer = undefined;
            }

            if (slideCount <= 1 || intervalMs <= 0 || motionQuery.matches) {
                return;
            }

            timer = window.setInterval(() => moveTo(activeIndex + 1), intervalMs);
        }

        previousButton?.addEventListener("click", () => {
            moveTo(activeIndex - 1);
            restartTimer();
        });

        nextButton?.addEventListener("click", () => {
            moveTo(activeIndex + 1);
            restartTimer();
        });

        indicators.forEach(indicator => {
            indicator.addEventListener("click", () => {
                const nextIndex = Number(indicator.dataset.homeHeroTo ?? "0");
                moveTo(Number.isFinite(nextIndex) ? nextIndex : 0);
                restartTimer();
            });
        });

        viewport.addEventListener("pointerdown", event => {
            if (event.pointerType === "mouse" || slideCount <= 1) {
                return;
            }

            suppressClick = false;
            swipeStartX = event.clientX;
            swipeStartY = event.clientY;
        });

        viewport.addEventListener("pointerup", event => {
            if (swipeStartX === undefined || swipeStartY === undefined) {
                return;
            }

            const deltaX = event.clientX - swipeStartX;
            const deltaY = event.clientY - swipeStartY;

            swipeStartX = undefined;
            swipeStartY = undefined;

            if (Math.abs(deltaX) < 42 || Math.abs(deltaX) < Math.abs(deltaY) * 1.25) {
                return;
            }

            suppressClick = true;
            moveTo(activeIndex + (deltaX < 0 ? 1 : -1));
            restartTimer();
        });

        viewport.addEventListener("click", event => {
            if (!suppressClick) {
                return;
            }

            event.preventDefault();
            suppressClick = false;
        }, true);

        viewport.addEventListener("pointercancel", () => {
            swipeStartX = undefined;
            swipeStartY = undefined;
            suppressClick = false;
        });

        track.addEventListener("transitionend", event => {
            if (event.propertyName !== "transform" || !shouldNormalizeAfterTransition) {
                return;
            }

            if (resetTimer !== undefined) {
                window.clearTimeout(resetTimer);
                resetTimer = undefined;
            }

            normalizeTrackPosition();
        });

        root.addEventListener("mouseenter", () => {
            if (timer !== undefined) {
                window.clearInterval(timer);
                timer = undefined;
            }
        });

        root.addEventListener("mouseleave", restartTimer);
        root.addEventListener("focusin", () => {
            if (timer !== undefined) {
                window.clearInterval(timer);
                timer = undefined;
            }
        });
        root.addEventListener("focusout", restartTimer);

        const observer = new ResizeObserver(() => setTrackPosition(false));
        observer.observe(viewport);

        moveTo(0, false);
        restartTimer();
    });
}
